import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const TimeToHireByRoleChart = ({ data, description }) => {
  const timeToHireByRoleData = () => {
    const roleGroups = {};
    
    // Filter for hired candidates with valid time to hire data
    data.filter(candidate => 
      candidate.stage === 'Hired' && 
      candidate.timeToHire && 
      candidate.timeToHire > 0 &&
      candidate.role &&
      candidate.role.trim() !== ''
    ).forEach(candidate => {
      const role = candidate.role;
      
      if (!roleGroups[role]) {
        roleGroups[role] = {
          role: role,
          totalDays: 0,
          count: 0,
          candidates: []
        };
      }
      
      roleGroups[role].totalDays += candidate.timeToHire;
      roleGroups[role].count += 1;
      roleGroups[role].candidates.push(candidate);
    });

    // Calculate averages and prepare chart data
    const chartData = Object.values(roleGroups)
      .filter(group => group.count >= 1) // Show roles with at least 1 hire
      .map(group => ({
        name: group.role.length > 20 ? group.role.substring(0, 18) + '...' : group.role, // Truncate long role names
        fullName: group.role, // Keep full name for tooltip
        days: Math.round(group.totalDays / group.count),
        count: group.count
      }))
      .sort((a, b) => a.days - b.days) // Sort by days ascending
      .slice(0, 10); // Show top 10 roles

    return chartData;
  };

  const chartData = timeToHireByRoleData();


  if (chartData.length === 0) {
    return (
      <div className="dashboard-card p-6 slide-in-up" style={{animationDelay: '500ms'}}>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold text-gray-800">Time to Hire by Role</h3>
          <div className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-1 rounded-full">
            Average Days
          </div>
        </div>
        {description && (
          <p className="text-sm text-gray-600 mb-4">{description}</p>
        )}
        <div className="flex items-center justify-center h-64 text-gray-500">
          <div className="text-center">
            <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p className="text-sm font-medium text-gray-900">No Data Available</p>
            <p className="text-xs text-gray-500 mt-1">Need at least 1 hired candidate per role</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-card p-6 slide-in-up" style={{animationDelay: '500ms'}}>
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-semibold text-gray-800">Time to Hire by Role</h3>
        <div className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-1 rounded-full">
          Average Days
        </div>
      </div>
      {description && (
        <p className="text-sm text-gray-600 mb-4">{description}</p>
      )}
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="name" tickLine={false} axisLine={false} style={{ fontSize: '12px' }} />
          <YAxis tickLine={false} axisLine={false} style={{ fontSize: '12px' }} />
          <Tooltip 
            contentStyle={{ 
              borderRadius: '8px', 
              border: 'none', 
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              fontSize: '12px',
              padding: '8px 12px'
            }} 
            formatter={(value, name) => {
              if (name === 'days') {
                return [`${value} days`, 'Time to Hire'];
              }
              return [value, name];
            }}
            labelFormatter={(label, items) => {
              // Use fullName from payload if available
              const item = items[0]?.payload;
              return item?.fullName || label;
            }}
          />
          <Bar 
            dataKey="days" 
            fill="#10b981" 
            name="Average Days"
            radius={[4, 4, 0, 0]}
            barSize={30}
          >
            {chartData.map((_, index) => {
              // Create proper opacity values for gradient effect
              const opacity = Math.max(0.3, 1 - (index * 0.1));
              return (
                <Cell 
                  key={`cell-${index}`} 
                  fill={`rgba(16, 185, 129, ${opacity})`} 
                />
              );
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TimeToHireByRoleChart;
